import { useState, type ReactNode } from 'react';
import { checkPermission } from '@cortex/core-types';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useAuth } from '../../../hooks/useAuth.js';
import { ProjectAssignModal } from './ProjectAssignModal.js';

/**
 * ボタンの寸法。`size="sm"`（32px）より一回り大きい **44px** に固定する。
 *
 * 44px は PMO看板で隣に並ぶタブスイッチ（`TabGroup` の `p-1` + `TabButton` の `py-2`/`text-sm`）
 * と同じ高さで、横の `px-4` も `TabButton` と同じ。並べたときに同じ設計の部品に見える。
 * 全体PJ看板にも同じ寸法を使い、2 つの看板でボタンの見た目を揃える
 * （置き場所ごとに寸法を変えると「同じ操作なのに別の部品」に見える）。
 *
 * ここを数値で持つ代わりに、タブスイッチ側の padding を変えたときは PMO看板の並びが崩れる。
 * その場合はこの値を追従させること。
 * @graph-connects none
 */
const ASSIGN_BUTTON_SIZE = 'h-11 px-4';

/**
 * ボタンの配色。塗りと文字色は `styles.css` の `--color-assign-action{,-text}` に置き、
 * テーマごとの値（dark=Backlog ロゴ色 + 濃緑文字 / light=要件定義アサイン トグルと同じ淡緑）は
 * CSS 側で切り替える。
 *
 * `variant="primary"`（アクセントの藍）のままだと看板内の主要導線（案件キーのリンク等）と同色で
 * アサインが埋もれる。意味色（success / warning）も使わない — 状態の表示ではないため。
 *
 * `hover:` / `active:` でも背景色を同じ変数へ固定しているのは、variant 側の
 * `active:bg-[var(--color-accent-active)]` を打ち消すため（打ち消さないと押した瞬間だけ藍に戻る）。
 * 押下のフィードバックは brightness で与える（`--color-backlog-action` を使う PJ Sandbox の
 * 取込ボタンと同じやり方）。
 * @graph-connects none
 */
const ASSIGN_BUTTON_COLOR = [
  'bg-[var(--color-assign-action)]',
  'hover:bg-[var(--color-assign-action)]',
  'active:bg-[var(--color-assign-action)]',
  'text-[var(--color-assign-action-text)]',
  'transition-[filter] hover:brightness-95 active:brightness-90',
].join(' ');

/**
 * 「案件をアサインする」ボタン + モーダル。全体PJ看板 / PMO看板 の右上に同じものを置く。
 *
 * **ボタンは権限が無いと描画しない**（PMOT 所属 / PDG の team_leader / admin のみ）。これは
 * 表示の整理であって防御ではない — API 側（`POST /projects/assign`）が同じ `ASSIGN_PROJECT` を
 * 再判定するのが本体。判定条件を 2 か所に書かず `checkPermission` に委ねる。
 *
 * 2 つの看板で同じボタンを出す要件なので、ボタン・モーダル・権限判定をこの 1 部品に閉じる
 * （各看板へコピーすると、片方だけ条件が変わってもう片方に反映されない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体PJ看板 / PMO看板 の「案件をアサインする」ボタン。ASSIGN_PROJECT 権限保持者にだけ表示しアサインモーダルを開く
 * @graph-connects useAuth [calls] ログインユーザーのロール・チーム取得
 * @graph-connects checkPermission [calls] ASSIGN_PROJECT 権限判定
 * @graph-connects ProjectAssignModal [calls] アサインモーダル描画
 */
export function ProjectAssignButton(): ReactNode {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!checkPermission(user?.portalRoles, 'ASSIGN_PROJECT', user?.teamName ?? undefined)) {
    return null;
  }

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        className={`shrink-0 whitespace-nowrap rounded-full ${ASSIGN_BUTTON_SIZE} ${ASSIGN_BUTTON_COLOR}`}
        onClick={() => {
          setOpen(true);
        }}
      >
        案件をアサインする
      </Button>
      <ProjectAssignModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
